import React, { useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { TemplateType } from '../types/inAppMessageTypes';
import { EditorPanel } from './InAppMessage/EditorPanel';
import { PhonePreview } from './InAppMessage/PhonePreview';
import { AudienceEditor } from './InAppMessage/AudienceEditor';
import { PayloadPreview } from './InAppMessage/PayloadPreview';
import { Edit3, Eye } from 'lucide-react';
import inAppMessageService from '../services/inAppMessage.service';
import { usePreferences } from '../contexts/PreferencesContext';
import { DEFAULT_DESTINATION_ID } from '../constants/inAppMessageDestinations';

const getInitialConfig = (t) => ({
  id: 'campaign_' + Date.now(),
  type: TemplateType.CENTER_MODAL,
  content: {
    title: t('pages.inAppMessages.defaults.title'),
    body: t('pages.inAppMessages.defaults.body'),
    imageUrl: 'https://picsum.photos/400/250',
    showImage: true,
    buttonText: t('pages.inAppMessages.defaults.buttonText'),
    showPrimaryButton: true,
    primaryAction: {
      type: 'screen',
      destinationId: DEFAULT_DESTINATION_ID,
    },
    secondaryButtonText: t('pages.inAppMessages.defaults.secondaryButtonText'),
    showSecondaryButton: true,
  },
  styles: {
    backgroundColor: '#ffffff',
    textColor: '#1e293b',
    buttonColor: '#2563EB',
    buttonTextColor: '#ffffff',
    secondaryButtonColor: '#ffffff',
    secondaryButtonTextColor: '#94a3b8',
    borderRadius: 16,
    overlayColor: '#00000099',
    titleFontSize: 20,
    bodyFontSize: 16,
    imageSize: 48,
    imageBorderRadius: 24,
  },
});

const InAppMessageManagement = () => {
  const theme = useTheme();
  const { t } = usePreferences();
  const [config, setConfig] = useState(() => getInitialConfig(t));
  const [currentStep, setStep] = useState('template');
  const [mobileTab, setMobileTab] = useState('editor');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showPayloadPreview, setShowPayloadPreview] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  // Store previous button states when switching templates
  const [previousButtonStates, setPreviousButtonStates] = useState({});

  const resetForm = () => {
    setConfig(getInitialConfig(t));
    setStep('template');
    setSelectedEmployeeIds([]);
    setEmployees([]);
    setShowPayloadPreview(false);
    setSendResult(null);
    setMobileTab('editor');
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, bgcolor: 'background.default', overflow: 'hidden' }}>
      
      {/* Mobile Top Navigation - Sticky */}
      <Box sx={{ 
        display: { xs: 'flex', md: 'none' }, 
        bgcolor: 'background.paper', 
        borderBottom: '1px solid', 
        borderColor: 'divider', 
        px: 1, 
        pt: 1,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'background.paper'
      }}>
        <button 
          onClick={() => setMobileTab('editor')}
          className={`flex-1 pb-2 text-sm font-medium border-b-2 flex items-center justify-center gap-2 ${mobileTab === 'editor' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500'}`}
        >
          <Edit3 size={16} /> {t('pages.inAppMessages.editor.mobileTabs.editor')}
        </button>
        <button 
          onClick={() => setMobileTab('preview')}
          className={`flex-1 pb-2 text-sm font-medium border-b-2 flex items-center justify-center gap-2 ${mobileTab === 'preview' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500'}`}
        >
          <Eye size={16} /> {t('pages.inAppMessages.editor.mobileTabs.preview')}
        </button>
      </Box>

      {/* Left Sidebar: Editor */}
      <Box sx={{ 
        display: { xs: mobileTab === 'editor' ? 'flex' : 'none', md: 'flex' }, 
        width: { xs: '100%', md: '400px' }, 
        flexShrink: 0, 
        height: '100%', 
        zIndex: 10, 
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {currentStep === 'audience' ? (
          <AudienceEditor
            selectedEmployeeIds={selectedEmployeeIds}
            onSelectionChange={(ids) => {
              setSelectedEmployeeIds(ids);
            }}
            onBack={() => {
              // Go back to previous step (style)
              setStep('style');
            }}
            onPreview={() => {
              // Fetch employees to get their info for preview
              inAppMessageService.getAudience(false).then(res => {
                setEmployees(res.data || []);
                setShowPayloadPreview(true);
                // Switch to preview tab on mobile
                setMobileTab('preview');
              }).catch(err => {
                console.error('Error fetching employees:', err);
                alert('Error loading employees. Please try again.');
              });
            }}
          />
        ) : (
          <EditorPanel 
            config={config} 
            setConfig={setConfig} 
            currentStep={currentStep}
            setStep={(s) => {
              setStep(s);
            }}
            previousButtonStates={previousButtonStates}
            setPreviousButtonStates={setPreviousButtonStates}
          />
        )}
      </Box>

      {/* Main Content: Preview or Payload Preview */}
      <Box sx={{ 
        display: { xs: mobileTab === 'preview' ? 'flex' : 'none', md: 'flex' }, 
        flex: 1, 
        position: 'relative', 
        bgcolor: 'background.default', 
        height: '100%', 
        overflow: 'hidden', 
        flexDirection: 'column' 
      }}>
        {showPayloadPreview ? (
          <PayloadPreview
            config={config}
            selectedEmployeeIds={selectedEmployeeIds}
            employees={employees}
            onBack={() => {
              setShowPayloadPreview(false);
              // On mobile, switch back to editor tab
              setMobileTab('editor');
            }}
            onEdit={() => {
              // Go back to first step (template)
              setShowPayloadPreview(false);
              setStep('template');
              setMobileTab('editor'); // Switch back to editor tab on mobile
            }}
            onSent={(result) => {
              setSendResult(result);
              setShowPayloadPreview(false);
              setTimeout(() => {
                resetForm();
              }, 3000);
            }}
          />
        ) : (
          <Box sx={{ 
            height: '100%', 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            p: { xs: 2, md: 4 } 
          }}>
            {sendResult && (
              <Box sx={{ 
                mb: 2, 
                p: 2, 
                bgcolor: 'success.light', 
                color: 'success.contrastText', 
                borderRadius: 1,
                textAlign: 'center'
              }}>
                {t('pages.inAppMessages.success.messagesSent')} {sendResult.result?.successCount || 0} {t('pages.inAppMessages.success.successful')}, {sendResult.result?.failureCount || 0} {t('pages.inAppMessages.success.failed')}.
              </Box>
            )}
            <Box sx={{ mb: 2, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
              <h2 className="text-2xl font-bold" style={{ color: theme.palette.text.primary }}>{t('pages.inAppMessages.preview.realtimePreview')}</h2>
              <p style={{ color: theme.palette.text.secondary }}>{t('pages.inAppMessages.preview.realtimePreviewDesc')}</p>
            </Box>
            <Box sx={{ 
              flex: 1, 
              width: '100%', 
              maxHeight: '800px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
               <PhonePreview config={config} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InAppMessageManagement;

